/**
 * The Cockpit's four figures — brief part 1 §6 band 2, audit v6 V6-01.
 *
 * Four cells in a row, a 2px ink rule under them, each a label (11px uppercase), ONE figure in the
 * numeric face at 30px and ONE context line. Which four is the team's (DEC-88): every figure is
 * computed in `./cockpit.ts` from a read the Cockpit already makes, none from the design documents
 * (leader ruling 1). The designer's own four were examples (sell-through, cover, value at stake,
 * measured benefit); the value at stake is the "Needs a decision" table's column and the hero's
 * sentence, and the others are reads this page does not make.
 *
 * **Each figure waits on its own read**, and none is a zero it did not read (ruling 5):
 *
 *   loading      «—» in `mark-neutral`, nothing under it
 *   answered empty, or the run is not known
 *                «—» in `mark-neutral`, and the context line says what is missing
 *   not published by this platform (a deliberate 404)
 *                data «B» (brief §4): `ArrivingFigure`, «—» with «In arrivo» — the reason is said
 *                once, in the page strip
 *   failed       «—»; the failure is the page strip's, with «Riprova»
 *
 * **The perimeter is stated above the figures** (G16): the in-season figures are one cycle run's,
 * the exit figures one position run's. The run itself is named in the top bar.
 */

import { useTranslation } from 'react-i18next';

import { formatCompactNumber, formatNumber } from '@/lib/format';
import { KpiLinkAbove, KpiLinkAnchor, useKpiLink } from '@/primitives';
import { AboutPopover, ArrivingFigure, dayWords, periodWords, type ReadState } from '@/planning';

import { percentOf, type CandidateFigures, type ExitFigures, type PriceFigures } from './cockpit';

const SHARE_DIGITS = 1;

export interface CockpitKpisProps {
  readonly exit: { readonly state: ReadState<unknown>; readonly figures: ExitFigures | null };
  readonly prices: { readonly state: ReadState<unknown>; readonly figures: PriceFigures | null };
  readonly candidates: { readonly state: ReadState<unknown>; readonly figures: CandidateFigures | null };
  /** What the in-season figures are over — the run on screen, when one is known. */
  readonly cycle: { readonly country: string; readonly period: string } | null;
  /** The stock date the exit figures measured, when the directory states it. */
  readonly exitAsOf: string | null;
  /**
   * Where the broken door positions are listed (ADR 9069): the run-wide list of the position run the figure counts, or `null`
   * when the Cockpit cannot name the consolidation run that list is opened under (`brokenDoorsHref`, `./cockpit`).
   */
  readonly brokenHref?: string | null;
}

/** A deliberate 404 for a read the platform has not built: data «B», not an absence. */
function notPublished(state: ReadState<unknown>): boolean {
  return state.kind === 'unavailable' && state.reason === 'not-built';
}

/** One cell: its label, its figure (or «—»), one context line. */
function Kpi({ labelKey, figure, title, context, arriving, descriptionKey, descriptionValues, href }: {
  readonly labelKey: string;
  /** What the figure really is — definition, source, as-of, limits (DEC-111, ADR 9047). */
  readonly descriptionKey: string;
  readonly descriptionValues?: Readonly<Record<string, string | number>>;
  /** The figure as shown, or `null` — «—», never `0`. */
  readonly figure: string | null;
  /** The exact figure, when `figure` is compact. */
  readonly title?: string;
  readonly context: string | null;
  readonly arriving: boolean;
  /** The list that holds exactly what the card counts (ADR 9069); never on an arriving or absent figure. */
  readonly href?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const link = useKpiLink(arriving || figure === null || href === null || href === undefined ? undefined : href);
  return (
    <div className={`border-hairline flex min-w-0 flex-col gap-1 border-e px-6 py-[18px] last:border-e-0${link.rootClass === '' ? '' : ` ${link.rootClass}`}`} data-kpi={labelKey}>
      {arriving ? <ArrivingFigure labelKey={labelKey} descriptionKey={descriptionKey} /> : (
        <>
          {/* Two lines, not one (usability audit D3): «POSIZIONI NEGOZIO CON TAGLIE ROTTE» was cut
              at 1440. A label longer still is clamped at two, whole in the title; the (i) beside it (DEC-111). */}
          <span className="flex min-w-0 items-start gap-1">
            <span {...link.labelProps} className="text-ink-label line-clamp-2 text-[11px] uppercase tracking-[0.08em] [overflow-wrap:anywhere]" title={t(labelKey)}>{t(labelKey)}</span>
            <KpiLinkAbove link={link}>
              <AboutPopover
                aboutKeys={[descriptionKey]}
                title={t(labelKey)}
                size="sm"
                {...(descriptionValues === undefined ? {} : { values: descriptionValues })}
              />
            </KpiLinkAbove>
          </span>
          <KpiLinkAnchor link={link}>
            <span
              {...link.valueProps}
              className={`font-numeric text-[30px] font-semibold leading-9 tabular-nums ${figure === null ? 'text-ink-secondary' : 'text-ink-primary'}`}
              title={title}
            >
              {figure ?? t('overview.tiles.notMeasured')}
            </span>
          </KpiLinkAnchor>
          {context !== null && <span className="text-ink-label line-clamp-2 text-[12px] [overflow-wrap:anywhere]" title={context}>{context}</span>}
        </>
      )}
    </div>
  );
}

export function CockpitKpis({ exit, prices, candidates, cycle, exitAsOf, brokenHref = null }: CockpitKpisProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const compact = (value: number): string => formatCompactNumber(value, i18n.language).text;
  const share = (value: number | null): string | null => (value === null
    ? null
    : `${formatNumber(value, i18n.language, { fractionDigits: SHARE_DIGITS, fixed: true })}%`);
  const residual = exit.figures === null ? null : formatCompactNumber(exit.figures.residualUnits, i18n.language);
  // «To decide» counts the PENDING steps, never the run's whole book (audit 2026-10-02 #1).
  const pending = prices.figures?.pending ?? null;
  const steps = pending === null ? null : formatCompactNumber(pending.steps, i18n.language);

  return (
    <section aria-label={t('overview.kpi.label')} className="flex flex-col gap-2" data-kpi-band="">
      <p className="text-caption text-ink-secondary flex flex-wrap gap-x-3">
        <span>{cycle === null ? t('overview.kpi.perimeterNoCycle') : t('overview.kpi.perimeterCycle', { country: cycle.country, period: periodWords(cycle.period, t, i18n.language) })}</span>
        <span>{exitAsOf === null ? t('overview.kpi.perimeterNoExit') : t('overview.kpi.perimeterExit', { date: dayWords(exitAsOf, i18n.language) })}</span>
      </p>
      <div className="border-ink-primary bg-surface-raised grid grid-cols-2 border-b-2 lg:grid-cols-4">
        <Kpi descriptionKey="help.cockpitKpis.residual"
          labelKey="overview.kpi.residual"
          figure={residual?.text ?? null}
          {...(residual === null ? {} : { title: residual.exact })}
          context={exit.figures === null ? null : t('overview.kpi.residualContext', { options: compact(exit.figures.options), doors: compact(exit.figures.doors) })}
          arriving={notPublished(exit.state)}
        />
        {/* Broken runs are what a consolidation repairs — the one figure here that asks for action. */}
        <Kpi descriptionKey="help.cockpitKpis.broken"
          labelKey="overview.kpi.broken"
          figure={exit.figures === null ? null : share(percentOf(exit.figures.brokenDoors, exit.figures.doors))}
          context={exit.figures === null ? null : t('overview.kpi.brokenContext', { broken: compact(exit.figures.brokenDoors), doors: compact(exit.figures.doors) })}
          arriving={notPublished(exit.state)}
          href={brokenHref}
        />
        <Kpi descriptionKey="help.cockpitKpis.steps"
          labelKey="overview.kpi.steps"
          figure={steps?.text ?? null}
          {...(steps === null ? {} : { title: steps.exact })}
          context={prices.figures === null || pending === null
            ? null
            : pending.steps === 0
              ? t('overview.kpi.stepsContextNone', { total: compact(prices.figures.steps) })
              : pending.articles === null || pending.firstWeek === null
                ? null
                : t('overview.kpi.stepsContext', { articles: pending.articles, week: periodWords(pending.firstWeek, t, i18n.language) })}
          arriving={notPublished(prices.state)}
        />
        <Kpi descriptionKey="help.cockpitKpis.solved"
          labelKey="overview.kpi.solved"
          figure={candidates.figures === null ? null : share(percentOf(candidates.figures.solved, candidates.figures.considered))}
          context={candidates.figures === null ? null : t('overview.kpi.solvedContext', {
            solved: compact(candidates.figures.solved), considered: compact(candidates.figures.considered), refused: compact(candidates.figures.refused),
          })}
          arriving={notPublished(candidates.state)}
        />
      </div>
    </section>
  );
}
